import { Injectable, NgZone } from '@angular/core';
import { Mutex } from 'async-mutex';
import {
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  fromEvent,
  map,
  Observable,
  switchMap,
  takeWhile,
  catchError,
  of,
} from 'rxjs';
import { cloneDeep, isEmpty } from 'lodash';
import { captureException } from '@sentry/angular';
// Krisp noise filter is dynamically loaded via DynamicModuleLoaderService
import type { KrispNoiseFilterProcessor } from '@livekit/krisp-noise-filter/dist/NoiseFilterProcessor';
import {
  Room,
  LocalParticipant,
  RoomEvent,
  ConnectionState,
  DisconnectReason,
  Track,
  RemoteTrackPublication,
  VideoQuality,
  Participant,
  RemoteParticipant,
  ConnectionQuality,
  LocalTrackPublication,
  LocalAudioTrack,
  TrackPublication,
  VideoCodec,
  ScalabilityMode,
  VideoEncoding,
  VideoPreset,
} from 'livekit-client';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Collaboration } from '@pncl/common-models';
import {
  ActionsQueryParamKeys,
  checkIfQueryParamSetToTrue,
  isPersonaSession,
} from '../common/utils/actions-query-params-util';
import {
  ActiveRTCDevices,
  AgentInfo,
  CallContext,
  CallError,
  CallParticipant,
  CallProvider,
  HIDE_TRACK_SYMBOL,
  ParticipantEventAction,
  RTCCloudRecording,
  RTCInterface,
  RTCRoomCreationOpts,
  RoomAuth,
  ScreenShareOptions,
  SubscribeCallQuality,
  TrackEventAction,
  TrackState,
  TrackType,
} from '../common/interfaces/rtc-interface';
import { SpaceRepository } from '../state/space.repository';
import { Session } from '../models/session';
import { DEFAULT_DEVICE_ID } from '../common/utils/devices-handle-util';
import { UserSystemStatus } from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { InterceptorSkipHeader } from '../auth.interceptor';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { DEFAULT_VIDEO_STREAM_CODEC } from '../models/device-manger';
import { LivekitAgentChatService } from './livekit-agent-chat.service';
import { ATTRIBUTE_PUBLISH_ON_BEHALF, LunaPresenceService } from './luna-presence.service';
import { UserService } from './user.service';
import { CustomerSupportService } from './customer-support.service';
import { ProviderStateService } from './provider-state.service';
import { SpacesService } from './spaces.service';
import { FLAGS, FlagsService } from './flags.service';
import { NoiseCancellationConfigurationService } from './noise-cancellation-configuration.service';
import { TelemetryService } from './telemetry.service';
import { NotificationToasterService } from './notification-toaster.service';
import { SpaceRecordingService } from './space-recording.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { DynamicModuleLoaderService, DynamicModuleKey } from './dynamic-module-loader.service';
import { ObserverModeService } from './observer-mode.service';
import { isObserverParticipant } from './observer-mode.utils';

export enum PendingSubscription {
  SUBSCRIBE,
  UNSUBSCRIBE,
}

const SERVERURL = 'wss://pencil.livekit.cloud';
const LIVEKIT_HOST = 'https://pencil.livekit.cloud';
const LIVEKIT_EGRESS_PATH = '/twirp/livekit.Egress/ListEgress';
const RECORDING_STATUS_CHECK_DELAY = 10000; // 10 seconds
const PENCIL_BOT_IDENTITY = 'pencil-bot';
const HIDDEN_PENCIL_BOT_IDENTITY = 'pencil-bot-hidden';
// LiveKit attribute an avatar "delegate" participant carries, pointing at the
// agent identity it publishes media on behalf of (its "principal"). The avatar
// plugin (Tavus) runs the avatar OUT OF PROCESS: it joins as a separate
// kind=agent participant that renders the video, while the real tutor agent
// (principal, "Luna") runs the brain. Both are kind=agent, so the FE would
// otherwise show two "Luna" tiles — one with video (delegate), one media-less
// (principal). We collapse them: hide the principal's media-less tile and let
// the delegate render as the single agent tile.
// Defined alongside the other tutor attributes in luna-presence.service, which reads it
// for the same principal-vs-delegate distinction.

enum RecordingStatusSource {
  LIVEKIT = 'livekit',
  PENCIL_BOT = 'pencil-bot',
}

/**
 * RPC method the AI tutor agent ("Luna") worker registers on its LiveKit
 * participant to support being removed from the call.
 *
 * Contract:
 *   - direction: local participant (this client) → agent participant
 *   - method:    'pencil.agent.leave'
 *   - payload:   '' (empty string; no arguments)
 *   - handler:   acks immediately, then disconnects itself from the room and
 *                tears down its session. Returns '{"ok":true}' (ignored here).
 *
 * Agent-side handler: pencil-agents/ai-tutor/main.py (registered in
 * entrypoint() after session.start()). If it isn't registered, performRpc
 * rejects with an UNSUPPORTED_METHOD error and removal surfaces as a failure
 * toast.
 */
export const AGENT_LEAVE_RPC_METHOD = 'pencil.agent.leave';
export const AGENT_PAUSE_RPC_METHOD = 'pencil.agent.pause';
export const AGENT_RESUME_RPC_METHOD = 'pencil.agent.resume';
export const AGENT_CANCEL_CREATION_RPC_METHOD = 'pencil.agent.cancel_creation';
export const AGENT_END_SESSION_RPC_METHOD = 'pencil.agent.end_session';

@Injectable({
  providedIn: 'root',
})
export class LivekitService extends RTCInterface implements RTCCloudRecording {
  readonly provider = CallProvider.LIVEKIT;

  private room!: Room;
  private roomAuth: RoomAuth | null = null;
  private joinLeaveMutex = new Mutex();
  private startOrStopRecordingMutex = new Mutex();
  private currentActiveEgressRequest?: string;
  private krispProcessor?: KrispNoiseFilterProcessor;
  private enableNoiseCancellationMutex = new Mutex();
  private startRecordingRetryTimeout?: NodeJS.Timer;
  private startRecordingRetryAttempt = 0;
  private readonly MAX_START_RECORDING_RETRIES = 5;
  private readonly INITIAL_RETRY_DELAY = 1000; // 1 second
  private activeRecordingSources = new Set<RecordingStatusSource>();

  public currentLocalParticipant?: LocalParticipant;

  onEndScreenSharing?: () => Promise<void>;

  // Hold any early subscription is made before remote track publication is sent
  // participantIdWithTrackSource = participantId (sid on LiveKit) + Source (Camera, Mic, ....)
  private pendingSubscriptions: { [participantIdWithTrackSource: string]: PendingSubscription } =
    {};

  private egressCheckAttempts = 0;
  private readonly MAX_EGRESS_CHECKS = 3;

  private recordingStatusCheckTimeout?: NodeJS.Timer;

  constructor(
    public customerSupportService: CustomerSupportService,
    private userService: UserService,
    private spacesService: SpacesService,
    private ngZone: NgZone,
    private spaceRepo: SpaceRepository,
    public flagsService: FlagsService,
    noiseCancellationConfigurationService: NoiseCancellationConfigurationService,
    telemetry: TelemetryService,
    translateService: TranslateService,
    notificationToasterService: NotificationToasterService,
    providerState: ProviderStateService,
    private http: HttpClient,
    private spaceRecordingService: SpaceRecordingService,
    deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private dynamicModuleLoader: DynamicModuleLoaderService,
    private observerModeService: ObserverModeService,
    private agentChatService: LivekitAgentChatService,
    private lunaPresenceService: LunaPresenceService,
  ) {
    super(
      flagsService,
      noiseCancellationConfigurationService,
      telemetry,
      translateService,
      notificationToasterService,
      providerState,
      customerSupportService,
      deviceAndBrowserDetectorService,
    );
    ngZone.runOutsideAngular(() => {
      // creates a new room with options
      this.room = new Room({
        // Manage updating stream quality based on current video width on our end
        // As LiveKit is super optimized & handling if the video element is being PIP, they will stop the stream
        adaptiveStream: false,

        // optimize publishing bandwidth and CPU for simulcasted tracks
        dynacast: true,
        disconnectOnPageLeave: false,
      });

      // Register the LiveKit text-stream handlers (lk.chat / lk.transcription) on
      // the room so the tutor's text replies + any typed chat flow into the
      // agent chat service. Handlers persist for the room's lifetime.
      this.agentChatService.attach(this.room);

      // Derive Luna's realtime state (lk.agent.state) off the SAME room — a single
      // subscription to the agent participant's attributes, streamed as an
      // observable for the UI. Persists for the room's lifetime.
      this.lunaPresenceService.attach(this.room);

      // Will be remove once having a stable LiveKit on prod
      Object.values(RoomEvent).forEach((state) => {
        if (
          [RoomEvent.ActiveSpeakersChanged, RoomEvent.ParticipantMetadataChanged].includes(state)
        ) {
          return;
        }
        this.room.on(state, (...args: any[]) => {
          console.log('Room Event', state, args);
        });
      });

      this.updateMetadata$().subscribe((metadata) => {
        this.setMetadata(metadata);
      });

      /**
       * Ignored TrackStreamStatedChanged event as our logic doesn't depend on handling this event
       * Ignored TrackPublished event as it just fires when a remote participant enables his cam or mic or starts a screensharing
       * but it doesn't have any information about the tracks itself which will come after with TrackSubscribed event. We can use
       * this event if we have a loading before showing the video (For example)
       *  */
      this.room
        // Local participant events
        .on(RoomEvent.ConnectionStateChanged, this.handleConnectionStateChanged.bind(this))
        .on(RoomEvent.Connected, this.handleCallIsConnected.bind(this))
        .on(RoomEvent.Reconnecting, this.handleReconnectingEvent.bind(this))
        .on(RoomEvent.SignalReconnecting, this.handleReconnectingEvent.bind(this))
        .on(RoomEvent.Reconnected, this.handleReconnectedEvent.bind(this))
        .on(RoomEvent.ConnectionQualityChanged, this.handleConnectionQualityChanged.bind(this))
        .on(RoomEvent.Disconnected, this.handleDisconnectedEvent.bind(this))
        .on(RoomEvent.LocalTrackPublished, this.handleLocalTrackEvent.bind(this))
        .on(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpubEvent.bind(this))

        // Remote participants events
        .on(RoomEvent.ParticipantDisconnected, this.handleParticipantLeaveEvent.bind(this))
        .on(RoomEvent.ParticipantConnected, this.handleParticipantJoinEvent.bind(this))

        // Track events
        // 1st stage: Publication events
        .on(RoomEvent.TrackPublished, this.handleTrackPubEvent.bind(this))
        .on(RoomEvent.TrackUnpublished, this.handleTrackUnpubEvent.bind(this))
        // 2nd stage: Subscription events
        .on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubEvent.bind(this))
        .on(RoomEvent.TrackSubscribed, this.handleTrackSubscribedEvent.bind(this))
        // 3rd stage: Muted/Unmuted events
        .on(RoomEvent.TrackMuted, this.handleTrackMutedEvent.bind(this))
        .on(RoomEvent.TrackUnmuted, this.handleTrackUnMutedEvent.bind(this))

        // Recording handling events
        .on(RoomEvent.ParticipantMetadataChanged, this.handleParticipantMetadataChange.bind(this))
        .on(
          RoomEvent.ParticipantAttributesChanged,
          this.handleParticipantAttributesChange.bind(this),
        )
        .on(RoomEvent.RecordingStatusChanged, (recording: boolean) =>
          this.handleRecordingStatusChange(recording, RecordingStatusSource.LIVEKIT),
        );
    });
  }

  // Added Reconnecting & SignalReconnecting state to the check as it still means call is connected
  // Also video call component is depending on its value for showing/hiding the entire component
  isConnected(): boolean {
    return [
      ConnectionState.Connected,
      ConnectionState.Reconnecting,
      ConnectionState.SignalReconnecting,
    ].includes(this.room.state);
  }

  isConnecting(): boolean {
    return this.room.state === ConnectionState.Connecting;
  }

  isDisconnecting(): boolean {
    return this.providerState.callDisconnecting;
  }

  /**
   * Load LiveKit auth for a space/room and prepare for joining a call.
   *
   * Behavior:
   * - If the current user is a host and automatic cloud recording is NOT enabled,
   *   force room creation by overriding `opts.skipRoomCreation = false`.
   *
   */
  async loadAuth(
    spaceId: string,
    breakoutRoomId: string | undefined,
    context: CallContext,
    isExtendExp = false,
    opts: RTCRoomCreationOpts = {
      skipRoomCreation: true,
      skipGenerateToken: false,
    },
  ): Promise<void> {
    // if we already have auth data for this room, do nothing
    if (!isExtendExp && this.isAuthValid(this.roomAuth, spaceId, breakoutRoomId)) {
      return;
    }

    // Generate a unique request ID for the current request
    const requestId = ++this.loadAuthCurrentRequestId;

    // Determine options taking into account host and automatic cloud recording settings
    if (
      this.spaceRepo.isCurrentUserHost() &&
      !this.spaceRecordingService.isAutomaticCloudRecordingEnabled
    ) {
      opts.skipRoomCreation = false;
    }

    if (!opts?.skipGenerateToken) {
      // reset roomAuth to make sure that it will not be used under any race condition
      // before the new one is created
      this.roomAuth = null;
    }

    const isObserver = this.observerModeService.isObserverModeActive();

    const userId: string | undefined = this.userService.user.getValue()?.user._id;
    if (userId) {
      const res = await firstValueFrom(
        this.spacesService.getVideoCallAuth(
          spaceId,
          breakoutRoomId,
          context,
          CallProvider.LIVEKIT,
          opts,
          isObserver,
        ),
      );

      if (!opts?.skipGenerateToken && requestId === this.loadAuthCurrentRequestId) {
        // set the room auth only if the request not being cancelled by another request

        this.roomAuth = (res?.body as any)?.data || null;
      }
    }
    if (!this.roomAuth || !this.roomAuth.token) {
      throw new Error('Could not get room data for LiveKit RTC');
    } else {
      this.providerState.callReady = true;
    }
  }

  protected constructRoomIdInRoomAuth(spaceId: string, breakoutRoomId?: string): string {
    if (!breakoutRoomId || Session.isMainRoom(breakoutRoomId, spaceId, true)) {
      return `${spaceId}-MainRoom`;
    } else {
      return `${spaceId}-${breakoutRoomId}`;
    }
  }

  getAuthData(): RoomAuth | null {
    return this.roomAuth;
  }

  setAuthData(data: RoomAuth | null): void {
    this.roomAuth = data;
    this.providerState.callReady = this.roomAuth ? true : false;
  }

  clearRoomAuthToken(): void {
    this.roomAuth = null;
  }

  doesAuthTokenExist(): boolean {
    return !!this.roomAuth && !!this.roomAuth.token;
  }

  async join(): Promise<void> {
    await this.ngZone.runOutsideAngular(async () => {
      await this.joinLeaveMutex.runExclusive(async () => {
        try {
          if (this.isConnected() || !this.roomAuth || !this.roomAuth.token) {
            return;
          }
          await this.room.connect(SERVERURL, this.roomAuth.token, {
            autoSubscribe: false,
          });
        } catch (joinCallError) {
          const joinCallReqStatusCode = ({ joinCallError } as any)?.joinCallError?.status;

          const isTokenExpired = joinCallReqStatusCode === 401;
          const isRoomNotExist = joinCallReqStatusCode === 404;

          if (!isTokenExpired && !isRoomNotExist) {
            captureException('[LiveKit] Connect to call failed', {
              extra: {
                error: joinCallError,
              },
            });
            this.handleDisconnectedEvent(DisconnectReason.JOIN_FAILURE);
            return;
          }

          if (!this.spaceRepo.activeSpaceId) {
            captureException('[LiveKit] No active space ID for room/auth creation');
            return;
          }

          try {
            await this.loadAuth(
              this.spaceRepo.activeSpaceId,
              this.spaceRepo.activeSpaceCurrentRoomUid,
              CallContext.SESSION,
              true,
              {
                skipRoomCreation: false,
                skipGenerateToken: isTokenExpired ? false : true,
              },
            );
          } catch (error) {
            this.logMeetingEvent('loadAuth error', {
              loadAuthError: error instanceof Error ? error.message : error,
            });
            this.handleDisconnectedEvent(DisconnectReason.JOIN_FAILURE);
            return;
          }

          this.joinLeaveMutex.cancel();
          this.join();
        }
      });
    });
  }

  async leave(): Promise<void> {
    await this.joinLeaveMutex.runExclusive(async () => {
      super.leave();
      if (!this.isConnected()) {
        return;
      }
      this.providerState.callDisconnecting = true;
      try {
        await this.room.disconnect();
      } catch (error) {
        /* empty catch block */
      } finally {
        this.providerState.callDisconnecting = false;
      }
    });
  }

  async publishAndStartBroadcastingVideoStream(mediaStreamTrack: MediaStreamTrack): Promise<void> {
    if (await this.reuseExistingLocalTrackPub(Track.Source.Camera, mediaStreamTrack)) {
      return;
    }

    await this.room.localParticipant.publishTrack(mediaStreamTrack, {
      simulcast: true,
      source: Track.Source.Camera,
      videoCodec: this.videoCodec,
      scalabilityMode: this.videoScalabilityMode,
      videoEncoding: this.videoEncoding,
      videoSimulcastLayers: this.videoSimulcastLayers,
    });
  }

  /** ***************** LocalVideo Track Publish Settings ****************************/

  get videoCodec(): VideoCodec {
    return (this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]?.video_codec ||
      DEFAULT_VIDEO_STREAM_CODEC) as VideoCodec;
  }

  get videoScalabilityMode(): ScalabilityMode | undefined {
    return this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]?.scalability_mode as
      | ScalabilityMode
      | undefined;
  }

  get videoEncoding(): VideoEncoding | undefined {
    return this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]
      ?.video_encoding as any as VideoEncoding | undefined;
  }

  get videoSimulcastLayers(): VideoPreset[] | undefined {
    const layer = this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]
      ?.video_simulcast_layer as any;
    if (!layer) {
      return undefined;
    }
    return [
      new VideoPreset(
        layer.width,
        layer.height,
        layer.maxBitrate,
        layer.maxFramerate,
        layer.priority,
      ),
    ];
  }

  /** ***************** End LocalVideo Track Publish Settings ****************************/

  private async publishAndEnableBroadcastingAudioStream(
    mediaStreamTrack: MediaStreamTrack,
  ): Promise<void> {
    if (await this.reuseExistingLocalTrackPub(Track.Source.Microphone, mediaStreamTrack)) {
      return;
    }

    await this.room.localParticipant.publishTrack(mediaStreamTrack, {
      source: Track.Source.Microphone,
    });
  }

  async enableNoiseCancellation(): Promise<void> {
    // Added a mutex here because the LocalTrackPublished event sometimes fires
    // twice simultaneously. The mutex prevents calling setEnabled/setProcessor simultaneously
    return this.enableNoiseCancellationMutex.runExclusive(async () => {
      // Music mode takes precedence — NC must not run while music mode is active
      if (this.isMusicModeEnabled) {
        return;
      }

      const localAudioTrackPub = this.room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      const localAudioTrack = localAudioTrackPub?.track;

      // Check support first (this will load the module if needed)
      const isSupported = await this.checkKrispSupportAsync();

      if (!isSupported) {
        console.warn('[Livekit] Krisp noise cancellation is not supported on this browser');
        return;
      }

      if (!this.isNoiseCancellationSupported() || !localAudioTrack) {
        return;
      }

      try {
        if (!this.krispProcessor) {
          // Load the Krisp module dynamically
          const krispModule = await this.loadVideoCallsModules();

          if (!krispModule) {
            console.warn('[Livekit] Failed to load Krisp noise filter module');
            return;
          }

          this.krispProcessor = krispModule.KrispNoiseFilter();
          await localAudioTrack.setProcessor(this.krispProcessor as any);
        } else {
          console.log('[Livekit] Noise cancellation processor is already set, enabling it');
          await this.krispProcessor.setEnabled(true);
        }

        this.telemetry.event('[Livekit Noise Cancellation]', {
          action: 'enabled',
        });
      } catch (error) {
        captureException(new Error('Could not enable noise cancellation'), {
          extra: error,
        });
        return;
      }

      if (!this.room.localParticipant.isMicrophoneEnabled) {
        return;
      }

      this.rebroadcastLocalTrack(localAudioTrackPub);
    });
  }

  async disableNoiseCancellation(): Promise<void> {
    const localAudioTrackPub = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );
    const localAudioTrack = localAudioTrackPub?.track;
    if (!this.isNoiseCancellationSupported() || !localAudioTrackPub || !localAudioTrack) {
      return;
    }

    await this.krispProcessor?.setEnabled(false);

    if (!this.room.localParticipant.isMicrophoneEnabled) {
      return;
    }

    this.rebroadcastLocalTrack(localAudioTrackPub);
  }

  async enableMusicMode(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    if (this.isNoiseCancellationEnabled) {
      await this.disableNoiseCancellation();
    }

    this.telemetry.event('[Livekit Music Mode]', { action: 'enabled' });
  }

  async disableMusicMode(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    if (this.isNoiseCancellationEnabled) {
      await this.enableNoiseCancellation();
    }

    this.telemetry.event('[Livekit Music Mode]', { action: 'disabled' });
  }

  async startBroadcastingVideoStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.unmute();
  }

  async startBroadcastingAudioStream(mediaStreamTrack: MediaStreamTrack): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );

    if (!localTrackPublication || localTrackPublication.track?.id !== mediaStreamTrack.id) {
      this.publishAndEnableBroadcastingAudioStream(mediaStreamTrack);
      return;
    }

    await localTrackPublication.unmute();
  }

  async stopBroadcastingVideoStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.mute();
  }

  async stopBroadcastingAudioStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.mute();
  }

  async setStreamQuality(
    participantId: string,
    type: TrackType,
    quality: SubscribeCallQuality,
  ): Promise<void> {
    const remoteTrackPublication = this.room
      .getParticipantByIdentity(participantId)
      ?.getTrackPublication(
        type === TrackType.VIDEO ? Track.Source.Camera : Track.Source.ScreenShare,
      ) as RemoteTrackPublication;

    if (!remoteTrackPublication) {
      return;
    }

    remoteTrackPublication.setVideoQuality(quality as unknown as VideoQuality);
  }

  isCameraEnabled(): boolean {
    return this.room.localParticipant.isCameraEnabled;
  }

  isMicEnabled(): boolean {
    return this.room.localParticipant.isMicrophoneEnabled;
  }

  public subscribe(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, true);
  }

  public unsubscribe(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, false);
  }

  public stage(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, false);
  }

  async startScreenShare(
    options?: ScreenShareOptions,
    onEnd?: () => Promise<void>,
    onError?: (error: any) => Promise<void>,
  ): Promise<any> {
    if (!this.room) {
      return;
    }
    if (onEnd) {
      this.onEndScreenSharing = onEnd;
    }
    const preferCurrentTab = options?.preferCurrentTab ?? false;
    const track = await this.room.localParticipant
      // Used featue flag variables directly as we're sure at this moment, user should have the correct values
      .setScreenShareEnabled(
        true,
        {
          audio: !preferCurrentTab,
          preferCurrentTab,
          // note: There is a bug in the element capture API that does not let you "unrestrict" the screenshare
          // once the screenshare is not the current tab so we will disable moving switching from current tab
          surfaceSwitching: options?.preferCurrentTab ? 'exclude' : 'include',
          resolution: {
            width: this.screenShareCaptureWidth,
            height: this.screenShareCaptureHeight,
            frameRate: this.screenShareCaptureMaxFPS,
          },
        },
        {
          videoCodec: this.screenShareEncodingCodec as VideoCodec,
          simulcast: this.isScreenShareSimulcastEnabled,
          name: options?.preferCurrentTab ? HIDE_TRACK_SYMBOL : undefined,
          screenShareEncoding: {
            maxBitrate: this.screenShareEncodingMaxBitrate,
            maxFramerate: this.screenShareEncodingMaxFPS,
            priority: 'medium',
          },
        },
      )
      .catch((error) => {
        if (onError) {
          onError(error);
        }
      });

    if (options?.restrictToWhiteboard && track?.track?.mediaStreamTrack) {
      await this.setupWhiteboardRestrictionListener(track.track.mediaStreamTrack);
    }
    return track;
  }

  attachAudioElement(
    participant: CallParticipant,
    trackType: TrackType,
    element: HTMLAudioElement,
  ): void {
    const liveKitParticipant = this.room.getParticipantByIdentity(participant.participantId);
    if (trackType === TrackType.VIDEO) {
      liveKitParticipant?.getTrackPublication(Track.Source.Microphone)?.audioTrack?.attach(element);
    } else if (trackType === TrackType.SCREEN) {
      liveKitParticipant
        ?.getTrackPublication(Track.Source.ScreenShareAudio)
        ?.audioTrack?.attach(element);
    }
  }

  async stopScreenShare(): Promise<any> {
    if (!this.room) {
      return;
    }
    return this.room.localParticipant.setScreenShareEnabled(false);
  }

  async startRecording(isAutomaticTrigger = false): Promise<void> {
    // Clear any existing retry attempts if this is a manual call
    if (!isAutomaticTrigger) {
      if (this.startRecordingRetryTimeout) {
        clearTimeout(this.startRecordingRetryTimeout);
        this.startRecordingRetryTimeout = undefined;
      }
      this.startRecordingRetryAttempt = 0;
    }

    this.startOrStopRecordingMutex.runExclusive(async () => {
      if (!this.spaceRepo.activeSpaceId || !this.spaceRepo.activeSpaceCurrentRoomUid) {
        return;
      }

      try {
        const startEgressRequest = await firstValueFrom(
          this.spacesService.changeCloudRecordingState(
            CallProvider.LIVEKIT,
            this.spaceRepo.activeSpaceId,
            this.room.name,
            true,
            this.isParticipantEgressType ? this.room.localParticipant.identity : undefined,
          ),
        );

        if (startEgressRequest?.data?.success) {
          this.currentActiveEgressRequest = startEgressRequest?.data?.recordingId;
          // Reset retry attempt on success
          this.startRecordingRetryAttempt = 0;

          // For participant egress type, we need to handle setting the recording status here
          // since we're ignoring the RecordingStatusChanged event
          if (this.isParticipantEgressType) {
            this.setMetadata({ recordingInitiator: undefined });
            this.providerState.recordingEvent = {
              _type: 'STARTED',
              initiatorParticipantId: undefined,
            };
          }
        }

        // This is to cover a quick start recording request was done while stop recording request is in progress
        // It will result in LiveKit won't emit RecordingStatusChanged again given recording starts again. So, this condition to update the initiator side
        if (this.room.isRecording && !this.isParticipantEgressType) {
          this.setMetadata({ recordingInitiator: this.isParticipantEgressType ? undefined : true });
          this.providerState.recordingEvent = {
            _type: 'STARTED',
            initiatorParticipantId: this.currentActiveEgressRequest
              ? this.room.localParticipant.identity
              : undefined,
          };
        }
      } catch (startRecordingError) {
        // @TODO [Elwakeel] add UX for failing start recording requests, also the same for stop recording requests
        captureException(startRecordingError);

        // Only attempt retry if this was an automatic trigger
        // and we haven't exceeded max retries
        if (
          isAutomaticTrigger &&
          this.startRecordingRetryAttempt < this.MAX_START_RECORDING_RETRIES
        ) {
          this.retryStartRecording();
        }
      }
    });
  }

  private retryStartRecording(): void {
    // Calculate delay with exponential backoff
    const delay = this.INITIAL_RETRY_DELAY * Math.pow(2, this.startRecordingRetryAttempt);

    this.logMeetingEvent('retrying_start_recording', {
      attempt: this.startRecordingRetryAttempt + 1,
      delay,
    });

    this.startRecordingRetryTimeout = modifiedSetTimeout(
      () => {
        this.startRecordingRetryAttempt++;
        this.startRecording(true); // Pass true to indicate this is an automatic retry
      },
      delay,
      true,
    ); // run outside zone
  }

  private handleParticipantEgressTrigger(): void {
    if (this.isParticipantEgressType) {
      this.startRecordingRetryAttempt = 0;
      this.startRecording(true); // Pass true to indicate this is an automatic trigger
    }
  }

  async stopRecording(): Promise<void> {
    this.startOrStopRecordingMutex.runExclusive(async () => {
      if (
        !this.spaceRepo.activeSpaceId ||
        !this.spaceRepo.activeSpaceCurrentRoomUid ||
        (!this.room.isRecording && !this.room.remoteParticipants.get(PENCIL_BOT_IDENTITY))
      ) {
        return;
      }

      try {
        const stopResponse = await firstValueFrom(
          this.spacesService.changeCloudRecordingState(
            CallProvider.LIVEKIT,
            this.spaceRepo.activeSpaceId,
            this.room.name,
            false,
            this.isParticipantEgressType ? this.room.localParticipant.identity : undefined,
            this.currentActiveEgressRequest,
          ),
        );
        // Make sure recordingIitiator is empty to cover the case of multiple CR toggles within the same call session
        this.setMetadata({ recordingInitiator: undefined });
        this.currentActiveEgressRequest = undefined;

        // For participant egress type, we need to handle setting the recording status here
        // since we're ignoring the RecordingStatusChanged event
        if (this.isParticipantEgressType && stopResponse?.data?.success) {
          this.providerState.recordingEvent = {
            _type: 'STOPPED',
          };
        }
      } catch (stopRecordingError) {
        captureException(stopRecordingError);
      }
    });
  }

  setLocalVideoCallStatsMetadata(userSystemStatus: UserSystemStatus): void {
    // Emit local metadata change before sending to server
    this.emitLocalParticipantMetadataChange(userSystemStatus);
    this.setMetadata(userSystemStatus);
  }

  toCallParticipant(participant: Participant): CallParticipant {
    return {
      participantId: participant.identity,
      screenShareTrackSid: participant.getTrackPublication(Track.Source.ScreenShare)?.trackSid,
      userId: participant.identity.split('$')[0],
      video: this.getTrackState(participant.getTrackPublication(Track.Source.Camera)),
      audio: this.getTrackState(participant.getTrackPublication(Track.Source.Microphone)),
      screen: this.getTrackState(participant.getTrackPublication(Track.Source.ScreenShare)),
      screenAudio: this.getTrackState(
        participant.getTrackPublication(Track.Source.ScreenShareAudio),
      ),
      videoTrack: participant.getTrackPublication(Track.Source.Camera)?.videoTrack
        ?.mediaStreamTrack,
      audioTrack: participant.getTrackPublication(Track.Source.Microphone)?.audioTrack
        ?.mediaStreamTrack,
      screenVideoTrack: participant.getTrackPublication(Track.Source.ScreenShare)?.videoTrack
        ?.mediaStreamTrack,
      screenAudioTrack: participant.getTrackPublication(Track.Source.ScreenShareAudio)?.audioTrack
        ?.mediaStreamTrack,
      local: participant.isLocal,
      joinedAt: participant.joinedAt,
      userSystemStatus: this.parseMetadata(participant.metadata),
      name: participant.name,
      agentInfo: this.getAgentInfo(participant),
    };
  }

  private getTrackState(track: TrackPublication | undefined): TrackState {
    if (!track || track.isMuted) {
      return 'off';
    } else if (track.trackName === HIDE_TRACK_SYMBOL) {
      return 'hidden';
    } else {
      return 'playable';
    }
  }

  async setDevices(devices: ActiveRTCDevices): Promise<void> {
    const devicePromises: Promise<boolean>[] = [];
    if (devices.micId) {
      devicePromises.push(this.room.switchActiveDevice('audioinput', devices.micId));
    }
    if (devices.cameraId) {
      devicePromises.push(this.room.switchActiveDevice('videoinput', devices.cameraId));
    }
    if (devices.speakerId) {
      devicePromises.push(this.room.switchActiveDevice('audiooutput', devices.speakerId));
    }
    await Promise.all(devicePromises);
  }

  // No need for cam/mic as we are using our local tracks manager for broadcasting tracks
  getCurrentInputDevices(): Promise<ActiveRTCDevices> {
    const activeRTCDevices: ActiveRTCDevices = {
      speakerId: '',
    };

    activeRTCDevices.speakerId = this.room.getActiveDevice('audiooutput') || '';

    return Promise.resolve(activeRTCDevices);
  }

  doesProviderSupportNoiseCancellation(): boolean {
    // Check if the module is already loaded, it should be loaded inside the session module component
    if (this.dynamicModuleLoader.isModuleLoaded(DynamicModuleKey.KRISP_NOISE_FILTER)) {
      const krispModule = this.dynamicModuleLoader.getCachedModule(
        DynamicModuleKey.KRISP_NOISE_FILTER,
      );
      return krispModule?.isKrispNoiseFilterSupported() ?? false;
    }

    return false;
  }

  /**
   * Dynamically loads the Krisp noise filter module.
   * Uses DynamicModuleLoaderService for caching and error handling.
   */
  async loadVideoCallsModules(): Promise<any | null> {
    return this.dynamicModuleLoader.loadModule(DynamicModuleKey.KRISP_NOISE_FILTER);
  }

  /**
   * Checks if Krisp noise cancellation is actually supported.
   * Loads the module if needed.
   */
  private async checkKrispSupportAsync(): Promise<boolean> {
    const krispModule = await this.loadVideoCallsModules();

    if (!krispModule) {
      return false;
    }

    return krispModule.isKrispNoiseFilterSupported();
  }

  // Note: this does not include the local participant
  getParticipants(): { [key: string]: CallParticipant } {
    const livekitParticipants = this.room.remoteParticipants;
    const callParticipants: { [key: string]: CallParticipant } = {};
    livekitParticipants.forEach((value: RemoteParticipant, key: string) => {
      if (value.identity === PENCIL_BOT_IDENTITY || value.identity === HIDDEN_PENCIL_BOT_IDENTITY) {
        return;
      }
      // Hide the agent principal when an avatar delegate is publishing its video —
      // the delegate is the single agent tile (see isAgentPrincipalWithDelegate).
      if (this.isAgentPrincipalWithDelegate(value)) {
        return;
      }
      callParticipants[key] = this.toCallParticipant(value);
    });
    return callParticipants;
  }

  /** The avatar "delegate" participant (if any) publishing media on behalf of
   * `principalIdentity` (i.e. an out-of-process Tavus avatar). */
  private getAvatarDelegateFor(principalIdentity: string): RemoteParticipant | undefined {
    if (!this.room) {
      return undefined;
    }
    for (const p of this.room.remoteParticipants.values()) {
      if (p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF] === principalIdentity) {
        return p;
      }
    }
    return undefined;
  }

  /** True for an agent "principal" whose video is published by a separate
   * avatar delegate participant. Such a principal has no media of its own, so
   * we hide its tile and let the delegate render as the single agent tile. */
  private isAgentPrincipalWithDelegate(participant: Participant): boolean {
    return (
      !!participant?.isAgent &&
      !participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF] &&
      !!this.getAvatarDelegateFor(participant.identity)
    );
  }

  getVideoDimensionsByIdentity(
    identity: string,
    trackType: TrackType,
  ): { width: number; height: number } {
    const participant = this.room.getParticipantByIdentity(identity);
    if (!participant) {
      return this._defaultVideoDimensions;
    }

    const trackPublication = participant.getTrackPublication(
      trackType === TrackType.VIDEO ? Track.Source.Camera : Track.Source.ScreenShare,
    );
    if (!trackPublication) {
      return this._defaultVideoDimensions;
    }

    return trackPublication.dimensions ?? this._defaultVideoDimensions;
  }

  getNumberOfParticipants(): number {
    let participantsCount = Object.values(this.getParticipants()).length;

    if (!this.observerModeService.isObserverModeActive()) {
      participantsCount++;
    }

    return participantsCount;
  }

  protected getLocalParticipant(): LocalParticipant | null {
    return this.room?.localParticipant ?? null;
  }

  // Events handlers

  // === Local participant events handlers ===

  // Handle only connecting event as other events are handled as explicit RoomEvent
  private handleConnectionStateChanged(connectionState: ConnectionState) {
    if (connectionState === ConnectionState.Connecting) {
      this.providerState.callConnecting = true;
    }
  }

  private handleCallIsConnected() {
    // setting the speaker id before joining the call not working if the speaker not the default
    this.forceSettingSpeakerDevice();
    this.providerState.callConnecting = false;
    this.providerState.callConnected = true;
    this.currentLocalParticipant = cloneDeep(this.room.localParticipant);
    this.broadcastLocalParticipant(true);
    this.broadcastLocalParticipantId(this.room.localParticipant);
    this.broadcastExistingParticipantEvents();
    if (this.room.isRecording) {
      this.handleRecordingStatusChange(true, RecordingStatusSource.LIVEKIT);
    }
    this.checkRecordingStatusWithDelay();
    this.handleParticipantEgressTrigger();
  }

  private handleReconnectingEvent() {
    this.providerState.callReconnecting = true;
  }

  private handleReconnectedEvent() {
    this.providerState.callReconnecting = false;
    // A LiveKit reconnect (resume or full reconnect) can drop the manual
    // subscriptions we hold under autoSubscribe:false without re-firing the
    // participant-join → subscribe flow that human tracks rely on. The AI tutor
    // ("Luna") and its Tavus avatar delegate are the media the student hears/sees,
    // so re-assert their subscriptions here. Strictly scoped to kind=agent
    // participants — human subscription behaviour is untouched.
    this.resubscribeAgentTracks();
  }

  /**
   * Re-subscribe to the AI tutor agent's audio (and the Tavus avatar delegate's
   * audio/video) after a reconnect, reusing the existing manual-subscription
   * path (`_subscribe`). GUARD: only kind=agent participants are touched, so this
   * never alters subscription behaviour for human participants. `_subscribe` is
   * idempotent and defers via `pendingSubscriptions` if a publication has not
   * arrived yet. No-ops when agent-join is disabled or no agent is present.
   */
  private resubscribeAgentTracks(): void {
    if (!this.room || !this.flagsService.isFlagEnabled(FLAGS.ENABLE_AGENT_JOIN_CALL)) {
      return;
    }
    this.room.remoteParticipants.forEach((participant) => {
      if (!participant.isAgent) {
        return;
      }
      // Principal agents publish audio directly; the Tavus delegate publishes the
      // avatar's audio + video. Re-subscribe both — a no-op for whichever the
      // participant does not publish.
      this._subscribe(participant.identity, TrackType.AUDIO, true);
      this._subscribe(participant.identity, TrackType.VIDEO, true);
    });
  }

  private handleConnectionQualityChanged(
    connectionQuality: ConnectionQuality,
    participant: Participant,
  ) {
    if (
      !participant.isLocal ||
      !this.providerState.callReconnectingValue ||
      ![ConnectionQuality.Excellent, ConnectionQuality.Good].includes(connectionQuality)
    ) {
      return;
    }
    // This is as a backup for reconnecting notification as we figured some cases that Reconnected event didn't be fired correctly
    this.providerState.callReconnecting = false;
  }

  private handleDisconnectedEvent(reason?: DisconnectReason | undefined) {
    // Only return if the disconnection happened while connecting to a call due to user rejected error (ConnectionErrorReason.NotAllowed)
    if (reason === DisconnectReason.USER_REJECTED && this.providerState.callConnecting) {
      return;
    }

    if (this.recordingStatusCheckTimeout) {
      clearTimeout(this.recordingStatusCheckTimeout);
      this.recordingStatusCheckTimeout = undefined;
    }

    this.providerState.callConnecting = false;
    this.providerState.callDisconnecting = false;
    this.providerState.callReconnecting = false;
    this.providerState.participantEvents = null;
    this.providerState.trackEvents = null;
    this.providerState.callConnected = false;
    // Reset the tutor chat/transcription history for the next session.
    this.agentChatService.clear();
    this.providerState.recordingEvent = {
      _type: 'STOPPED',
    };
    this.activeRecordingSources.clear();
    this.broadcastLocalParticipant(false);
    this.currentLocalParticipant = undefined;
    this.pendingSubscriptions = {};
    this.currentActiveEgressRequest = undefined;
    this.egressCheckAttempts = 0;
    this.dismissVideoCallNotifications();
    this.krispProcessor = undefined;

    switch (reason) {
      // We are handling Disconnected event using its explicit event (Participant left)
      case DisconnectReason.CLIENT_INITIATED:
        break;
      case DisconnectReason.SERVER_SHUTDOWN:
        this.providerState.callErrors = CallError.SERVICEDOWN;
        break;
      case DisconnectReason.JOIN_FAILURE:
        this.providerState.callErrors = CallError.TIME_OUT;
        break;
      default:
        this.providerState.callErrors = CallError.DISCONNECTED;
    }
  }

  private async handleLocalTrackEvent(
    pub: LocalTrackPublication,
    participant: Participant,
  ): Promise<void> {
    // Reflect the correct Noise Cancellation setting once local track is published as per LiveKit documentation
    // Link: https://docs.livekit.io/guides/enhanced-noise-cancellation/
    if (
      pub.source === Track.Source.Microphone &&
      pub.track instanceof LocalAudioTrack &&
      !this.isMusicModeEnabled
    ) {
      if (this.isNoiseCancellationEnabled) {
        this.enableNoiseCancellation();
      } else {
        this.disableNoiseCancellation();
      }
    }

    // Don't play local tracks if publication is muted
    if (pub.isMuted) {
      return;
    }

    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    this.publishTrackEvent(TrackEventAction.START, participant, pub);
  }

  private handleLocalTrackUnpubEvent(pub: TrackPublication, participant: Participant): void {
    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  // === Remote participants events handlers ===

  private handleParticipantJoinEvent(participant: Participant): void {
    // If the participant is an agent and the flag is not enabled, we skip adding it
    if (participant.isAgent && !this.flagsService.isFlagEnabled(FLAGS.ENABLE_AGENT_JOIN_CALL)) {
      return;
    }

    // When an avatar delegate joins, its principal's (media-less) tile — created
    // moments earlier when the principal joined alone — is now redundant. Tear
    // it down so only the delegate's video tile remains.
    const behalf = participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF];
    if (behalf) {
      const principal = this.room.getParticipantByIdentity(behalf);
      if (principal) {
        this.publishParticipantEvent(ParticipantEventAction.LEFT, principal);
      }
    }

    this.handleParticipantMetadataChange(participant.metadata, participant);
    this.publishParticipantEvent(ParticipantEventAction.JOINED, participant);
  }

  private handleParticipantLeaveEvent(participant: Participant): void {
    this.publishParticipantEvent(ParticipantEventAction.LEFT, participant);
  }

  // === Track events handlers ===

  private handleTrackPubEvent(pub: RemoteTrackPublication, participant: RemoteParticipant): void {
    // Broadcast participant updated event on each new publication to make participants manager list up-to-date
    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    if (isEmpty(this.pendingSubscriptions)) {
      return;
    }

    const pendingSubscriptionId = participant.identity + pub.source;

    if (
      pub.isSubscribed ||
      this.pendingSubscriptions[pendingSubscriptionId] === undefined ||
      this.pendingSubscriptions[pendingSubscriptionId] !== PendingSubscription.SUBSCRIBE
    ) {
      return;
    }

    pub.setSubscribed(true);
  }

  private handleTrackUnpubEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackUnsubEvent(
    event: Track,
    pub: TrackPublication,
    participant: Participant,
  ): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackSubscribedEvent(
    event: Track,
    pub: TrackPublication,
    participant: Participant,
  ): void {
    this.broadcastUnMutedSubscribedEvents(pub, participant);
  }

  private handleTrackMutedEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackUnMutedEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.START, participant, pub);
  }

  /**
   * Participant attributes carry the agent's identity (e.g. `agent.name`), which the worker
   * publishes AFTER it has joined the room. Without re-publishing an UPDATE here, nothing
   * re-derives `agentInfo` and every consumer (video tile label, orb, cursor attribution)
   * stays stuck on the stale/fallback values.
   */
  private handleParticipantAttributesChange(
    _changedAttributes: Record<string, string>,
    participant: Participant,
  ): void {
    if (
      participant.identity === PENCIL_BOT_IDENTITY ||
      participant.identity === HIDDEN_PENCIL_BOT_IDENTITY
    ) {
      return;
    }
    // Hidden avatar principal — don't resurrect its tile via an attributes UPDATE.
    if (this.isAgentPrincipalWithDelegate(participant)) {
      return;
    }
    // Process remote participants attribute changes only
    if (participant.isLocal) {
      return;
    }
    try {
      this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    } catch (error) {
      captureException(error);
    }
  }

  // We are just using Participant metadata for processing cloud recording start
  // We needed to handle it using this way as LiveKit doesn't support an explicit way to broadcast recording initiator
  private handleParticipantMetadataChange(metadata: string | undefined, participant: Participant) {
    if (
      participant.identity === PENCIL_BOT_IDENTITY ||
      participant.identity === HIDDEN_PENCIL_BOT_IDENTITY
    ) {
      return;
    }
    // Hidden avatar principal — don't resurrect its tile via a metadata UPDATE.
    if (this.isAgentPrincipalWithDelegate(participant)) {
      return;
    }
    try {
      // Process remote participants metadata changes only
      if (participant.isLocal) {
        return;
      }

      const newParticipantMetadata = this.parseMetadata(participant.metadata);

      this.providerState.participantMetadataChange = this.toCallParticipant(participant);

      // Check if metadata update is related to cloud recording
      if (
        this.providerState.recordingEvent?._type !== 'STARTED' &&
        !!newParticipantMetadata.recordingInitiator === true
      ) {
        this.providerState.recordingEvent = {
          _type: 'STARTED',
          initiatorParticipantId: participant.identity,
        };
      }
    } catch (error) {
      captureException(error);
    }
  }

  private handleRecordingStatusChange(recording: boolean, source: RecordingStatusSource): void {
    // Skip the logic if we're using participant egress type
    // For participant egress type, recording state will be handled directly in the
    // startRecording and stopRecording methods based on API response success
    if (this.isParticipantEgressType) {
      return;
    }

    if (recording) {
      // Mark this source as actively recording.
      this.activeRecordingSources.add(source);
    } else {
      // Mark this source as no longer actively recording.
      this.activeRecordingSources.delete(source);

      // If there is still at least one active source (e.g. LiveKit or Pencil bot),
      // we should not treat this as a global "stop".
      if (this.activeRecordingSources.size > 0) {
        return;
      }
    }

    // Update Room metadata to let other users know there is an active recording
    // Decided to do it on the client side as receving a recording status change with true means recording actually starts
    if (recording && this.currentActiveEgressRequest) {
      this.setMetadata({ recordingInitiator: this.isParticipantEgressType ? undefined : true });
    }

    if (recording) {
      this.providerState.recordingEvent = {
        _type: 'STARTED',
        initiatorParticipantId: this.currentActiveEgressRequest
          ? this.room.localParticipant.identity
          : undefined,
      };
      return;
    }

    this.currentActiveEgressRequest = undefined;
    this.providerState.recordingEvent = {
      _type: 'STOPPED',
    };
  }

  // Helper functions

  private async reuseExistingLocalTrackPub(
    source: Track.Source,
    mediaStreamTrack: MediaStreamTrack,
  ): Promise<boolean> {
    const localTrackPub = this.room.localParticipant.getTrackPublication(source);
    const localTrack =
      source === Track.Source.Camera ? localTrackPub?.videoTrack : localTrackPub?.audioTrack;

    if (!localTrack || !localTrackPub) {
      return false;
    }

    await localTrack.replaceTrack(mediaStreamTrack, {
      userProvidedTrack: true,
    });
    localTrack.unmute();

    // Simulate stop & start on local side as local track publication isn't working like the remote one
    this.rebroadcastLocalTrack(localTrackPub);

    return true;
  }

  // It's mainly used to reflect the local track if it's changed/processed
  private rebroadcastLocalTrack(localTrackPublication: LocalTrackPublication) {
    this.publishTrackEvent(
      TrackEventAction.STOP,
      this.room.localParticipant,
      localTrackPublication,
    );
    this.publishTrackEvent(
      TrackEventAction.START,
      this.room.localParticipant,
      localTrackPublication,
    );
  }

  private broadcastUnMutedSubscribedEvents(pub: TrackPublication, participant: Participant) {
    /**
     * To cover all already active tracks after joining the call
     * Also it's used to start any track event
     * */
    if (!pub.isMuted) {
      this.publishTrackEvent(TrackEventAction.START, participant, pub);
    }
  }

  private broadcastLocalParticipantId(participant: Participant) {
    this.providerState.localParticipantId = participant.identity;
  }

  private publishParticipantEvent(action: ParticipantEventAction, participant: Participant) {
    const skipLocalObserverEvent = isObserverParticipant(participant);
    if (participant.identity === HIDDEN_PENCIL_BOT_IDENTITY || skipLocalObserverEvent) {
      // Hidden bot should not appear in UI or affect recording state
      return;
    }

    if (participant.identity === PENCIL_BOT_IDENTITY) {
      if (action === ParticipantEventAction.JOINED) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.PENCIL_BOT);
      } else if (action === ParticipantEventAction.LEFT) {
        this.handleRecordingStatusChange(false, RecordingStatusSource.PENCIL_BOT);
      }
      return;
    }
    // Avatar principal: suppress its (media-less) tile. LEFT is allowed through
    // so an already-shown principal tile can be torn down once its delegate
    // arrives (see handleParticipantJoinEvent).
    if (action !== ParticipantEventAction.LEFT && this.isAgentPrincipalWithDelegate(participant)) {
      return;
    }
    const participantEvent = {
      action: action,
      participant: this.toCallParticipant(participant),
    };
    this.providerState.participantEvents = participantEvent;
  }

  private publishTrackEvent(
    action: TrackEventAction,
    liveKitParticipant: Participant,
    publication: TrackPublication,
  ): void {
    if (this.getTrackType(publication.source) === TrackType.SCREEN) {
      this.publishParticipantEvent(ParticipantEventAction.UPDATED, liveKitParticipant);

      // Handle reflecting the correct UI after stopping sharing from the bottom toolbar
      // Only for local participant if he owns this track stopped event for screen sharing
      if (
        liveKitParticipant.isLocal &&
        action === TrackEventAction.STOP &&
        this.onEndScreenSharing
      ) {
        this.onEndScreenSharing();
      }
    }
    const trackEvent = {
      action: action,
      participant: this.toCallParticipant(liveKitParticipant),
      type: this.getTrackType(publication.source),
    };
    this.providerState.trackEvents = trackEvent;
    if (liveKitParticipant.isLocal) {
      this.publishParticipantEvent(ParticipantEventAction.UPDATED, liveKitParticipant);
    }
  }

  private getTrackType(source: Track.Source): TrackType {
    switch (source) {
      case Track.Source.Camera:
        return TrackType.VIDEO;
      case Track.Source.Microphone:
        return TrackType.AUDIO;
      case Track.Source.ScreenShare:
        return TrackType.SCREEN;
      case Track.Source.ScreenShareAudio:
        return TrackType.SCREEN_AUDIO;
      default:
        return TrackType.VIDEO;
    }
  }

  private async forceSettingSpeakerDevice() {
    if (!this.deviceAndBrowserDetectorService.isSetSinkIdSupported()) {
      return;
    }
    const speakerId = this.getSelectedSpeakerDeviceId() || DEFAULT_DEVICE_ID;
    try {
      await this.room.switchActiveDevice('audiooutput', speakerId);
    } catch (error) {
      console.error('failed to switch audio output when joining the call', error);
    }
  }

  private broadcastExistingParticipantEvents(): void {
    const participants = this.getParticipants();
    Object.entries(participants).forEach(([_, participant]) => {
      // To cover the case of a CR is already started before local user's join as LiveKit isn't broadcasting any event happened before your join
      const liveKitParticipant = this.room.getParticipantByIdentity(participant.participantId);

      // If the participant is an agent and the flag is not enabled, we skip adding it
      if (
        liveKitParticipant?.isAgent &&
        !this.flagsService.isFlagEnabled(FLAGS.ENABLE_AGENT_JOIN_CALL)
      ) {
        return;
      }

      this.providerState.participantEvents = {
        action: ParticipantEventAction.JOINED,
        participant,
      };

      if (liveKitParticipant) {
        this.handleParticipantMetadataChange(liveKitParticipant.metadata, liveKitParticipant);
      }
    });
    this.updateRecordingStatusFromPencilBotPresence();
  }
  private updateRecordingStatusFromPencilBotPresence(): void {
    this.room.remoteParticipants.forEach((participant) => {
      if (participant.identity === PENCIL_BOT_IDENTITY) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.PENCIL_BOT);
        return;
      }
    });
  }

  private broadcastLocalParticipant(isJoined: boolean) {
    this.providerState.participantEvents = {
      action: isJoined ? ParticipantEventAction.JOINED : ParticipantEventAction.LEFT,
      participant: this.toCallParticipant(
        isJoined
          ? this.room.localParticipant
          : this.currentLocalParticipant ?? this.room.localParticipant,
      ),
    };
  }

  private _subscribe(id: string, trackType: TrackType, enabled: boolean): void {
    let source: Track.Source;
    switch (trackType) {
      case TrackType.AUDIO:
        source = Track.Source.Microphone;
        break;
      case TrackType.VIDEO:
        source = Track.Source.Camera;
        break;
      case TrackType.SCREEN:
        source = Track.Source.ScreenShare;
        break;
      case TrackType.SCREEN_AUDIO:
        source = Track.Source.ScreenShareAudio;
        break;
      default:
        throw new Error('Track type not supported');
    }
    const remoteParticipant = this.room.getParticipantByIdentity(id);
    const remoteTrackPublication = remoteParticipant?.getTrackPublication(
      source,
    ) as RemoteTrackPublication;
    if (remoteTrackPublication) {
      if (
        !this.flagsService.isFlagEnabled(FLAGS.USE_SET_ENABLED_FOR_VIDEO_TRACKS) ||
        (enabled && !remoteTrackPublication.isSubscribed)
      ) {
        // first subscribe
        remoteTrackPublication.setSubscribed(enabled);
      }
      if (this.flagsService.isFlagEnabled(FLAGS.USE_SET_ENABLED_FOR_VIDEO_TRACKS)) {
        if (remoteTrackPublication.isEnabled === enabled) {
          return;
        }
        remoteTrackPublication.setEnabled(enabled);
        if (enabled) {
          this.broadcastUnMutedSubscribedEvents(remoteTrackPublication, remoteParticipant!);
        } else {
          this.handleTrackMutedEvent(remoteTrackPublication, remoteParticipant!);
        }
      }
    }

    // Sometimes remote publications take some time to be sent to remote participants
    // In such case we will maintain a map for all requested subscriptions that can't find available remote publication
    // Then do the subscription on receving the remote track publication
    if (enabled) {
      this.pendingSubscriptions[id + source] = PendingSubscription.SUBSCRIBE;
    } else {
      delete this.pendingSubscriptions[id + source];
    }
  }

  // ---- Metadata ----

  private setMetadata(nextMetadata: LivekitParticipantMetadata) {
    const currentMetadataString = this.room.localParticipant.metadata;
    if (!currentMetadataString) {
      this.room.localParticipant.setMetadata(JSON.stringify(nextMetadata)).catch(() => {
        // be silent, it's not critical if it fails
      });
    } else {
      const currentMetadata: LivekitParticipantMetadata = JSON.parse(currentMetadataString);
      const updatedMetadata = JSON.stringify({ ...currentMetadata, ...nextMetadata });
      this.room.localParticipant.setMetadata(updatedMetadata).catch(() => {
        // be silent, it's not critical if it fails
      });
    }
  }

  private parseMetadata(metadata: string | undefined): LivekitParticipantMetadata {
    return metadata ? JSON.parse(metadata) : {};
  }

  private updateMetadata$(): Observable<LivekitParticipantMetadata> {
    return fromEvent(this.room, RoomEvent.Connected).pipe(
      switchMap(() => this.getParticipantMetadata$()),
    );
  }

  private getParticipantMetadata$(): Observable<LivekitParticipantMetadata> {
    return combineLatest([
      this.spaceRepo.isCurrentUserHost$,
      this.spaceRecordingService.isPrivacyAwareCloudRecordingEnabled$,
    ]).pipe(
      map(([isUserHost, isPrivacyAwareCloudRecordingEnabled]) => {
        // Determine if participant video should be recorded
        let shouldRecordParticipantVideo: boolean;
        if (!isPrivacyAwareCloudRecordingEnabled) {
          shouldRecordParticipantVideo = true;
        } else {
          // If privacy feature is enabled, only hosts' video should be recorded
          shouldRecordParticipantVideo = isUserHost;
        }

        // Always update metadata with isHost, userId, and tabId regardless of recording logic
        const userId = this.userService.userId;
        const tabId = this.userService.userUniqueHash;

        return {
          isHost: isUserHost,
          userId,
          tabId,
          shouldRecordParticipantVideo,
        };
      }),
      distinctUntilChanged(
        (prev, curr) =>
          prev.isHost === curr.isHost &&
          prev.userId === curr.userId &&
          prev.tabId === curr.tabId &&
          prev.shouldRecordParticipantVideo === curr.shouldRecordParticipantVideo,
      ),
      takeWhile(() => this.isConnected()),
    );
  }

  private logMeetingEvent(action: string, payload: Record<string, any> = {}): void {
    this.telemetry.event('[Meeting Event]', {
      action,
      ...payload,
    });
  }

  private shouldRecoverFromBrokenRecordingState(participant: Participant): boolean {
    return (
      participant.isLocal &&
      this.spaceRecordingService.isAutomaticCloudRecordingEnabled &&
      this.providerState.recordingEvent?._type !== 'STARTED' &&
      this.egressCheckAttempts < this.MAX_EGRESS_CHECKS
    );
  }

  private getEgressStatus(): Observable<{ items: Array<{ status: string }> } | null> {
    return this.http
      .post<{ items: Array<{ status: string }> }>(
        `${LIVEKIT_HOST}${LIVEKIT_EGRESS_PATH}`,
        { roomName: this.roomAuth?.roomId },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.roomAuth?.token}`,
          })
            .set(ErrorInterceptorSkipHeader, '')
            .set(InterceptorSkipHeader, ''),
        },
      )
      .pipe(
        catchError((error) => {
          captureException(new Error('Failed to check egress status'), {
            extra: { error },
          });
          return of(null);
        }),
      );
  }

  private checkRecordingStatusWithDelay(): void {
    this.recordingStatusCheckTimeout = modifiedSetTimeout(
      () => {
        if (this.shouldRecoverFromBrokenRecordingState(this.room.localParticipant)) {
          this.syncLivekitRecordingStatus();
        }
      },
      RECORDING_STATUS_CHECK_DELAY,
      true, // run outside zone
    );
  }

  private async syncLivekitRecordingStatus(): Promise<void> {
    this.logMeetingEvent('recovering_from_broken_recording_state', {
      room_recording_state: this.room.isRecording,
    });

    const response = await firstValueFrom(this.getEgressStatus());

    if (response?.items) {
      const hasActiveEgress = response.items.some(
        (egress) => egress.status === 'EGRESS_ACTIVE' || egress.status === 'EGRESS_STARTING',
      );
      if (hasActiveEgress) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.LIVEKIT);
      } else {
        this.logMeetingEvent('no_active_egress_found', {
          attempt_number: this.egressCheckAttempts + 1,
        });
        this.checkRecordingStatusWithDelay();
      }
    }

    this.egressCheckAttempts++;
  }

  private get isParticipantEgressType(): boolean {
    return (
      this.spaceRepo.activeSpace?.institution?.settings?.crAdvancedSettings?.egressType ===
      Collaboration.Spaces.EgressType.PARTICIPANT
    );
  }

  // Not supported methods

  // Deprecated: It was used before with the old service for managing tracks
  isSubscribed!: (participantId: string, trackType: TrackType) => string | boolean;

  async preJoin(): Promise<void> {
    return;
  }
  startTranscription() {
    console.warn('transcription is not supported with livekit');
  }
  stopTranscription() {
    console.warn('transcription is not supported with livekit');
  }
  lowerOutgoingVideoQuality(): void {
    console.warn('[TO BE IMPLEMENTED] :lowerIncomingVideoQuality');
  }
  resetOutgoingVideoQuality(): void {
    console.warn('[TO BE IMPLEMENTED] :resetIncomingVideoQuality');
  }

  private getAgentInfo(participant: Participant): AgentInfo | undefined {
    // `agent.name` is a participant attribute the worker publishes for Practice
    // Roleplay personas (see docs/superpowers/specs/
    // 2026-08-27-roleplay-identity-vct-diarization-design.md) — absent for the real AI
    // tutor, which keeps the 'Luna' fallback everywhere this is consumed.
    //
    // In Roleplay the persona joins the room a few seconds BEFORE the worker publishes its
    // real name (it's still resolving the character/onboarding at that point) — 'Luna' is
    // that product's actual name, not a loading placeholder, so showing it briefly for a
    // persona is actively wrong, not just imprecise. Fall back to '' there instead. Consumers
    // must prefer this over the raw LiveKit `participant.name` (a static 'Luna' set at
    // room-join time) — see user-tile.ts's `createDummyUser`, which agent tiles derive their
    // display name from.
    if (!participant.isAgent) {
      return undefined;
    }
    // Real roleplay tiles show the persona's own name (blank until the persona publishes
    // `agent.name`). A partner persona embed with no name configured yet falls back to the
    // default "Luna" (like the tutor), per the persona-mode default.
    const blankNameFallback =
      checkIfQueryParamSetToTrue(ActionsQueryParamKeys.ROLEPLAY) && !isPersonaSession();
    return {
      type: 'sparky',
      name: participant.attributes?.['agent.name'] || (blankNameFallback ? '' : 'Luna'),
    };
  }

  /**
   * The LIVE tutor principal to target for agent RPCs (leave / pause / resume).
   * Excludes the Tavus avatar DELEGATE (it carries {@link ATTRIBUTE_PUBLISH_ON_BEHALF}
   * and registers no RPC handlers), and prefers the most-recently-joined principal
   * so a lingering ZOMBIE principal (from a crashed/killed worker that LiveKit keeps
   * until its departure timeout) never wins the selection and swallow RPCs.
   */
  private findLiveAgent(): Participant | undefined {
    if (!this.room) {
      return undefined;
    }
    return Array.from(this.room.remoteParticipants.values())
      .filter((p) => p.isAgent && !p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF])
      .sort((a, b) => (b.joinedAt?.getTime() ?? 0) - (a.joinedAt?.getTime() ?? 0))[0];
  }

  /**
   * Whether a tutor is already on the call. Read from the room rather than the app's
   * participant state, which is why it is trustworthy at connect time: LiveKit populates
   * `remoteParticipants` from the join response BEFORE emitting RoomEvent.Connected (the
   * same reason {@link broadcastExistingParticipantEvents} can enumerate it there). It is
   * also unaffected by FLAGS.ENABLE_AGENT_JOIN_CALL, which filters agents out of the app's
   * participant events but not out of the room.
   */
  override hasAiAgentOnCall(): boolean {
    return Boolean(this.findLiveAgent());
  }

  /**
   * Removes the AI tutor agent ("Luna") from the call by asking it to leave
   * over a LiveKit RPC. The agent worker handles {@link AGENT_LEAVE_RPC_METHOD}
   * by disconnecting itself. Rejects if no agent is present or the RPC fails
   * (e.g. the agent has not registered the handler yet).
   */
  override async setLocalAttributes(attributes: Record<string, string>): Promise<void> {
    // Merge-by-key on the local participant. Remote participants (the agent worker) see
    // these via ParticipantAttributesChanged, the same channel the worker uses in reverse
    // for agent.name / agent.userId.
    if (!this.room?.localParticipant) {
      return;
    }
    await this.room.localParticipant.setAttributes(attributes);
  }

  override async removeAiAgent(): Promise<void> {
    const agent = this.findLiveAgent();
    if (!agent) {
      throw new Error('No AI tutor present in the call to remove');
    }
    await this.room.localParticipant.performRpc({
      destinationIdentity: agent.identity,
      method: AGENT_LEAVE_RPC_METHOD,
      payload: '',
    });
  }

  /**
   * Pause or resume the AI tutor ("Luna") over a LiveKit RPC. Pause asks the
   * agent to stop listening AND speaking (the LiveKit toggle-audio pattern),
   * keeping the session alive; resume re-enables both. Rejects if no agent is
   * present or the RPC fails.
   */
  override async setAiAgentPaused(paused: boolean): Promise<void> {
    const agent = this.findLiveAgent();
    if (!agent) {
      throw new Error('No AI tutor present in the call');
    }
    await this.room.localParticipant.performRpc({
      destinationIdentity: agent.identity,
      method: paused ? AGENT_PAUSE_RPC_METHOD : AGENT_RESUME_RPC_METHOD,
      payload: '',
    });
  }

  /** Ask the AI tutor ("Luna") to stop making a visual or app, over a LiveKit RPC. */
  override async cancelAiAgentCreation(creationId: string): Promise<void> {
    const agent = this.findLiveAgent();
    if (!agent) {
      throw new Error('No AI tutor present in the call');
    }
    await this.room.localParticipant.performRpc({
      destinationIdentity: agent.identity,
      method: AGENT_CANCEL_CREATION_RPC_METHOD,
      payload: JSON.stringify({ id: creationId }),
    });
  }

  /**
   * Ask the AI tutor ("Luna") to end the session now over a LiveKit RPC. The agent wraps
   * up in its own words and calls its own `end_session` tool; the structured report arrives
   * back via the existing `deliverSessionEnd` RPC. Rejects if no agent is present or the
   * RPC fails.
   */
  override async requestAiAgentEndSession(): Promise<void> {
    const agent = this.findLiveAgent();
    if (!agent) {
      throw new Error('No AI tutor present in the call to end the session');
    }
    await this.room.localParticipant.performRpc({
      destinationIdentity: agent.identity,
      method: AGENT_END_SESSION_RPC_METHOD,
      payload: '',
    });
  }

  override getRoomName(): string | undefined {
    return this.room?.name || undefined;
  }
}

type LivekitParticipantMetadata = Partial<
  {
    recordingInitiator: boolean;
    shouldRecordParticipantVideo: boolean;
    isHost: boolean;
    userId: string;
    tabId: string;
  } & UserSystemStatus
>;
